import { useEffect } from 'react';

import type { EditorContainerWidth as WidthPluginState } from '@atlaskit/editor-common/types/editor-container-width';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { pluginKey } from '../../pm-plugins/plugin-key';
import { useRefreshWidthOnTransition } from './useRefreshOnTransition';

export const setEditorWidth =
	(props: Partial<WidthPluginState>) =>
	(editorView: EditorView): void => {
		const {
			dispatch,
			state: { tr },
		} = editorView;

		tr.setMeta(pluginKey, props);

		dispatch(tr);
	};

export const useResizeWidthObserver = ({
	editorView,
	containerElement,
}: {
	containerElement: HTMLElement | null;
	editorView: EditorView;
}): void => {
	useRefreshWidthOnTransition(containerElement);

	useEffect(() => {
		const newState: Partial<WidthPluginState> = {
			lineLength: editorView.dom.clientWidth,
			width: containerElement?.offsetWidth,
		};
		setEditorWidth(newState)(editorView);
	}, [editorView, editorView.dom.clientWidth, containerElement]);

	useEffect(() => {
		const resizeWidth: ResizeObserverCallback = (entries) => {
			if (entries.length !== 1) {
				return;
			}
			const [fullAreaSize] = entries;

			if (!fullAreaSize || !Array.isArray(fullAreaSize.borderBoxSize)) {
				return;
			}

			const width = fullAreaSize.borderBoxSize[0].inlineSize;

			const lineLength = editorView.dom.clientWidth;

			setEditorWidth({
				width,
				lineLength,
			})(editorView);
		};

		const resizeObserver = new ResizeObserver(resizeWidth);
		if (containerElement) {
			resizeObserver.observe(containerElement);
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, [containerElement, editorView]);
};
